// New-lead inline modal — invoked from the customer command palette's
// empty state. Debounces duplicate detection while typing.
// Extracted from QuoteBuilder.jsx (Wave K refactor).
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { UserPlus, X, AlertTriangle } from "lucide-react";
import api from "../../../lib/api";

export default function NewLeadInlineModal({ open, seed, onClose, onCreate }) {
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", company: "", post_code: "" });
  const [dups, setDups] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setF({ first_name: "", last_name: "", email: "", phone: "", company: "", post_code: "" }); setDups([]); return; }
    // Seed intelligently: if seed contains @ it's an email; otherwise treat as name
    const s = (seed || "").trim();
    if (s.includes("@")) setF((prev) => ({ ...prev, email: s }));
    else if (/^\+?\d[\d\s()-]{4,}$/.test(s)) setF((prev) => ({ ...prev, phone: s }));
    else if (s) {
      const [first, ...rest] = s.split(/\s+/);
      setF((prev) => ({ ...prev, first_name: first, last_name: rest.join(" ") }));
    }
  }, [open, seed]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      const p = new URLSearchParams();
      if (f.email) p.set("email", f.email);
      if (f.phone) p.set("phone", f.phone);
      const nm = [f.first_name, f.last_name].filter(Boolean).join(" ").trim();
      if (nm) p.set("name", nm);
      if (!p.toString()) { setDups([]); return; }
      try {
        const r = await api.get(`/leads/dup-check?${p.toString()}`);
        setDups(r.data.matches || []);
      } catch { setDups([]); }
    }, 350);
    return () => clearTimeout(t);
  }, [open, f.email, f.phone, f.first_name, f.last_name]);

  if (!open) return null;
  const valid = f.first_name.trim() && f.email.trim();
  const emailDup = dups.find((d) => d.match_on === "email");

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    try { await onCreate(f); }
    catch (err) { alert(err?.response?.data?.detail || "Failed to create lead"); }
    finally { setBusy(false); }
  };

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        display: "grid", placeItems: "center",
        background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)",
      }}
      data-testid="palette-new-lead-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        style={{
          width: "min(480px, 92vw)", background: "#0b0f17",
          border: "1px solid rgba(255,255,255,0.10)", borderRadius: 14,
          padding: 20, boxShadow: "0 40px 80px rgba(0,0,0,0.6)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <UserPlus className="w-4 h-4" style={{ color: "#7dd3fc" }} />
          <div className="t2-mono" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 2, color: "#7dd3fc" }}>New Lead</div>
          <button
            type="button"
            onClick={onClose}
            style={{ marginLeft: "auto", background: "none", border: "none", color: "var(--t2-grey3)", cursor: "pointer" }}
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <h3 style={{ fontSize: 20, fontWeight: 700, color: "var(--t2-white)", margin: "10px 0 14px" }}>
          Capture a net-new customer
        </h3>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <label style={{ fontSize: 11, color: "var(--t2-grey2)" }}>
            First name*
            <input required autoFocus className="t2-input" value={f.first_name}
              data-testid="palette-lead-first-name"
              onChange={(e) => setF({ ...f, first_name: e.target.value })} />
          </label>
          <label style={{ fontSize: 11, color: "var(--t2-grey2)" }}>
            Last name
            <input className="t2-input" value={f.last_name}
              data-testid="palette-lead-last-name"
              onChange={(e) => setF({ ...f, last_name: e.target.value })} />
          </label>
          <label style={{ gridColumn: "span 2", fontSize: 11, color: "var(--t2-grey2)" }}>
            Email*
            <input required type="email" className="t2-input" value={f.email}
              data-testid="palette-lead-email"
              onChange={(e) => setF({ ...f, email: e.target.value })} />
          </label>
          <label style={{ fontSize: 11, color: "var(--t2-grey2)" }}>
            Mobile
            <input className="t2-input" value={f.phone}
              data-testid="palette-lead-phone"
              onChange={(e) => setF({ ...f, phone: e.target.value })} />
          </label>
          <label style={{ fontSize: 11, color: "var(--t2-grey2)" }}>
            Postcode
            <input className="t2-input" value={f.post_code}
              data-testid="palette-lead-postcode"
              onChange={(e) => setF({ ...f, post_code: e.target.value })} />
          </label>
          <label style={{ gridColumn: "span 2", fontSize: 11, color: "var(--t2-grey2)" }}>
            Company (optional)
            <input className="t2-input" value={f.company}
              data-testid="palette-lead-company"
              onChange={(e) => setF({ ...f, company: e.target.value })} />
          </label>
        </div>

        {emailDup && (
          <div
            data-testid="palette-lead-email-dup"
            style={{ marginTop: 12, padding: 10, borderRadius: 8, border: "1px solid rgba(239,68,68,0.40)", background: "rgba(239,68,68,0.08)" }}
          >
            <div className="t2-mono" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 2, color: "#fca5a5", marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
              <AlertTriangle className="w-3 h-3" /> Email already exists
            </div>
            <div style={{ fontSize: 12, color: "var(--t2-grey1)" }}>
              <b style={{ color: "var(--t2-white)" }}>{emailDup.full_name || emailDup.email}</b> already uses this email. Saving will <b>attach the existing {emailDup.is_lead ? "lead" : "customer"}</b> — no duplicate is created.
            </div>
          </div>
        )}

        {dups.length > 0 && (
          <div
            data-testid="palette-lead-dups"
            style={{
              marginTop: 12, padding: 10, borderRadius: 8,
              border: "1px solid rgba(245,158,11,0.30)", background: "rgba(245,158,11,0.06)",
            }}
          >
            <div className="t2-mono" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 2, color: "#fbbf24", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
              <AlertTriangle className="w-3 h-3" /> Possible duplicate{dups.length === 1 ? "" : "s"} ({dups.length})
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {dups.slice(0, 4).map((d) => (
                <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                  <span style={{ color: "var(--t2-white)", fontWeight: 600, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.full_name}</span>
                  <span className="t2-mono" style={{ fontSize: 9, padding: "1px 5px", borderRadius: 4, background: "rgba(255,255,255,0.06)", color: "var(--t2-grey2)", textTransform: "uppercase", letterSpacing: 1 }}>{d.match_on}</span>
                  <span style={{ color: "var(--t2-grey3)", fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 150 }}>{d.email}</span>
                  <span className="t2-mono" style={{
                    fontSize: 9.5, padding: "2px 6px", borderRadius: 4,
                    background: d.is_lead ? "rgba(56,189,248,0.15)" : "rgba(16,185,129,0.15)",
                    color: d.is_lead ? "#7dd3fc" : "#6ee7b7",
                    border: d.is_lead ? "1px solid rgba(56,189,248,0.40)" : "1px solid rgba(16,185,129,0.40)",
                  }}>{d.is_lead ? "LEAD" : "DEAL"}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14, paddingTop: 12, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <button type="button" onClick={onClose} className="t2-btn-ghost">Cancel</button>
          <button
            type="submit"
            disabled={!valid || busy}
            data-testid="palette-lead-submit"
            className="t2-btn-primary"
            style={{
              opacity: (!valid || busy) ? 0.4 : 1,
              pointerEvents: (!valid || busy) ? "none" : "auto",
              background: "#0ea5e9", borderColor: "#0ea5e9",
            }}
          >
            {busy ? "Creating…" : "Create + attach"}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
